package com.playoffpulse.shared.logic

import com.playoffpulse.shared.model.MagicStatus
import com.playoffpulse.shared.model.MagicSummary

const val PLAYOFF_BERTH_CODE = "PLAYOFF_BERTH"

sealed class PlayoffObjective(
    val code: String,
) {
    data object PlayoffBerth : PlayoffObjective(PLAYOFF_BERTH_CODE)

    data class SeatGoal(
        val seatCode: String,
    ) : PlayoffObjective(seatCode)
}

object PlayoffObjectiveResolver {
    fun displaySeatCode(summary: MagicSummary): String? {
        return highestHeldSeat(summary) ?: summary.playoffPositionCode
    }

    fun highestHeldSeat(summary: MagicSummary): String? {
        return highestHeldSeat(
            playoffPositionCode = summary.playoffPositionCode,
            conferenceHomeIceTag = summary.conferenceHomeIceTag,
            presidentsCupTag = summary.presidentsCupTag,
        )
    }

    fun highestHeldSeat(
        playoffPositionCode: String?,
        conferenceHomeIceTag: String?,
        presidentsCupTag: String?,
    ): String? {
        val seats = listOfNotNull(
            presidentsCupTag?.takeIf { it.isNotBlank() },
            conferenceHomeIceTag?.takeIf { it.isNotBlank() },
            playoffPositionCode?.takeIf { it.isNotBlank() },
        )

        if (seats.isEmpty()) return null
        return seats.minByOrNull { seatRank(it) }
    }

    fun nextGoalCode(summary: MagicSummary): String? {
        summary.nextClinchTargetSeatCode?.let { return it }
        val heldSeat = highestHeldSeat(summary) ?: return null
        return nextGoalCode(heldSeat, summary.team.division)
    }

    fun nextGoalCode(
        heldSeat: String,
        teamDivision: String,
    ): String? = when {
        heldSeat == "NHL1" -> null
        heldSeat == "EAST1" || heldSeat == "WEST1" -> "NHL1"
        heldSeat.matches(Regex("^(ATL|METRO|CEN|PAC)[123]$")) -> {
            val divisionCode = heldSeat.dropLast(1)
            val seed = heldSeat.last().digitToInt()
            if (seed > 1) {
                "$divisionCode${seed - 1}"
            } else if (divisionCode == "ATL" || divisionCode == "METRO") {
                "EAST1"
            } else {
                "WEST1"
            }
        }

        heldSeat.matches(Regex("^[EW]WC[12]$")) -> {
            val wildCardSeed = heldSeat.last().digitToInt()
            if (wildCardSeed == 2) {
                heldSeat.dropLast(1) + "1"
            } else {
                "${divisionCode(teamDivision)}3"
            }
        }

        else -> null
    }

    fun activeObjective(
        status: MagicStatus,
        teamDivision: String,
        playoffPositionCode: String?,
        conferenceHomeIceTag: String?,
        presidentsCupTag: String?,
    ): PlayoffObjective? = when (status) {
        MagicStatus.ELIMINATED -> null
        MagicStatus.CLINCHED -> {
            val heldSeat = highestHeldSeat(
                playoffPositionCode = playoffPositionCode,
                conferenceHomeIceTag = conferenceHomeIceTag,
                presidentsCupTag = presidentsCupTag,
            ) ?: return null

            nextGoalCode(heldSeat, teamDivision)?.let { PlayoffObjective.SeatGoal(it) }
        }

        MagicStatus.IN_POSITION,
        MagicStatus.CONTROLS_DESTINY,
        MagicStatus.CHASING
            -> PlayoffObjective.PlayoffBerth
    }

    fun divisionCode(division: String): String = when {
        division.equals("Atlantic", ignoreCase = true) -> "ATL"
        division.equals("Metropolitan", ignoreCase = true) -> "METRO"
        division.equals("Central", ignoreCase = true) -> "CEN"
        division.equals("Pacific", ignoreCase = true) -> "PAC"
        else -> division.take(3).uppercase()
    }

    private fun seatRank(code: String): Int = when {
        code == "NHL1" -> 0
        code == "EAST1" || code == "WEST1" -> 1
        code.matches(Regex("^(ATL|METRO|CEN|PAC)1$")) -> 2
        code.matches(Regex("^(ATL|METRO|CEN|PAC)2$")) -> 3
        code.matches(Regex("^(ATL|METRO|CEN|PAC)3$")) -> 4
        code.matches(Regex("^[EW]WC1$")) -> 5
        code.matches(Regex("^[EW]WC2$")) -> 6
        else -> 99
    }
}
